export type Attribute = {
  name: string;
  values: string[];
};

export type VariantOption = Record<string, string>;

export type VariantRow = {
  id: string; // stable id from options (hash)
  options: VariantOption;
  sku: string;
  barcode: string;
  price: number;
  inheritsBasePrice?: boolean;
  compareAt: number | null;
  cost: number | null;
  weight: number | null;
  stockQty: number;
  stockQuantity: number;
  isActive: boolean;
  currency?: string; // Optional currency for backend compatibility
  trackInventory?: boolean; // Optional inventory tracking flag
  isDefault?: boolean; // Optional default variant flag
  tierPrices?: Record<string, number>;
  sortOrder?: number;
};

function normalizeOptionValue(value: unknown): string {
  if (value && typeof value === "object" && "value" in (value as any)) {
    return String((value as any).value ?? "");
  }

  return String(value ?? "");
}

export function generateVariantId(options: VariantOption): string {
  const entries = Object.entries(options).sort(([a], [b]) =>
    a.localeCompare(b),
  );
  return entries.map(([k, v]) => `${k}:${normalizeOptionValue(v)}`).join("|");
}

function getOptionCode(val: unknown): string {
  const str = String(
    val && typeof val === "object" && "value" in val
      ? (val as any).value
      : (val ?? ""),
  );
  if (!str) return "";
  // Use first letter if it's a known size or color, otherwise just uppercase
  const first = str.charAt(0).toUpperCase();
  // If it's a single word and short, use it, otherwise first letter
  return str.length <= 3 ? str.toUpperCase() : first;
}

function normalizeAttributes(attributes: Attribute[]): Attribute[] {
  const byName = new Map<string, Attribute & { valueKeys: Set<string> }>();

  attributes.forEach((attribute) => {
    const name = attribute.name.trim();
    if (!name) return;

    const nameKey = name.toLowerCase();
    let target = byName.get(nameKey);
    if (!target) {
      target = { name, values: [], valueKeys: new Set<string>() };
      byName.set(nameKey, target);
    }

    attribute.values.forEach((rawValue) => {
      const value = String(rawValue ?? "").trim();
      if (!value) return;

      const valueKey = value.toLowerCase();
      if (target!.valueKeys.has(valueKey)) return;
      target!.valueKeys.add(valueKey);
      target!.values.push(value);
    });
  });

  return Array.from(byName.values())
    .map(({ valueKeys: _valueKeys, ...attribute }) => attribute)
    .filter((attribute) => attribute.values.length > 0);
}

export function generateVariants(
  attributes: Attribute[],
  baseSku: string | undefined,
  defaultPrice: number,
  defaultStock: number,
): VariantRow[] {
  const activeAttributes = normalizeAttributes(attributes);
  if (!activeAttributes.length) return [];

  const normalizedPrice = Number.isFinite(defaultPrice) ? defaultPrice : 0;
  const normalizedStock = Number.isFinite(defaultStock)
    ? Math.max(0, defaultStock)
    : 0;

  const names = activeAttributes.map((a) => a.name);
  const lists = activeAttributes.map((a) => a.values);
  const combos = cartesian<string>(lists);
  const stockAllocation = distributeStock(normalizedStock, combos.length);

  return combos.map((vals, index) => {
    const options: VariantOption = {};
    vals.forEach((v, i) => {
      options[names[i]] = v;
    });

    // Generate SKU: BaseSKU-Code1-Code2 or VAR-Code1-Code2
    const codes = vals.map(getOptionCode).filter(Boolean);
    const sku = baseSku
      ? `${baseSku}-${codes.join("-")}`
      : `VAR-${codes.join("-")}`;
    const seededStock = stockAllocation[index] ?? 0;

    return {
      id: generateVariantId(options),
      options,
      sku,
      barcode: "",
      price: normalizedPrice,
      inheritsBasePrice: true,
      compareAt: null,
      cost: null,
      weight: null,
      // Preserve the entered total inventory without normalizing it into each new row.
      stockQty: seededStock,
      stockQuantity: seededStock,
      isActive: true,
      sortOrder: index,
    };
  });
}

function cartesian<T>(arrays: T[][]): T[][] {
  if (arrays.length === 0) return [];
  return arrays.reduce<T[][]>(
    (acc, curr) => {
      const res: T[][] = [];
      for (const a of acc) {
        for (const b of curr) {
          res.push([...a, b]);
        }
      }
      return res;
    },
    [[]],
  );
}

function distributeStock(totalStock: number, rowCount: number): number[] {
  if (rowCount <= 0) return [];

  // Invalid fractional inventory should remain visible for validation instead of
  // being silently rounded away while a merchant regenerates the matrix.
  if (!Number.isInteger(totalStock)) {
    return Array.from({ length: rowCount }, (_, index) =>
      index === 0 ? totalStock : 0,
    );
  }

  const baseQuantity = Math.floor(totalStock / rowCount);
  const remainder = totalStock % rowCount;
  return Array.from(
    { length: rowCount },
    (_, index) => baseQuantity + (index < remainder ? 1 : 0),
  );
}

export function mergeVariants(
  existing: VariantRow[],
  generated: VariantRow[],
): VariantRow[] {
  const byId = new Map(existing.map((v) => [v.id, v] as const));
  const byOptions = new Map(
    existing
      .map((v) => [generateVariantId(v.options), v] as const)
      .filter(([signature]) => signature.length > 0),
  );

  const seenGenerated = new Set<string>();

  return generated
    .filter((variant) => {
      const signature = generateVariantId(variant.options);
      if (seenGenerated.has(signature)) return false;
      seenGenerated.add(signature);
      return true;
    })
    .map((g, index) => {
      const match =
        byId.get(g.id) ?? byOptions.get(generateVariantId(g.options));
      return { ...g, sortOrder: index, ...(match || {}) };
    });
}
